import 'arrayq';
import { createSelector } from 'reselect';

import { IEmailMessage, IEntity, IQuestion, IQuestionDB, IUsergroup, isNote, isQuestion } from '../../types';
import { bookmarksResource } from '../bookmarks/resource';
import { categoriesResource } from '../categories/resource';
import { categoriesSelector, factiongroupsSelector, originSelector, participantgroupsSelector, participantsSelector, searchTermSelector, sortingSelector, statusSelector } from '../filter/selectors';
import { participantIdSelector } from '../../participantid/selectors';
import { notesResource } from '../notes/resource';
import { notesSharedResource } from '../noteshared/resource';
import { emailArchiveResource } from '../../emailarchive/emails/resource';
import { participantsResource } from '../../participant/resource';
import { usergroupResource } from '../../usergroup/resource';
import Origin from '../filter/Origin';
import { questionsResource } from '../question/resource';
import { beheerResource } from '../../admin/resource';
import { statusGepubliceerd, typeRoleAdmin, typeRoleClerk, typeRolePracticioner } from '../question/authmodule/authcheck';

function wildcardMatcher(searchTerm: string) {
    if (searchTerm) {
        let term = searchTerm;
        term = term.replace(/[\\]/g, '\\\\');
        term = term.replace(/[\.\?\{\}\[\]\^\$]/g, (x) => `\\${x}`);
        term = term.replace(/\*/g, (x) => `.*`);
        term = term.replace(/\s+/g, ' ');
        term = term.split(' ').qSelect((x) => `(?=.*${x})`).join('');
        const rx = new RegExp(term, 'i');
        return (text: string) => rx.test(text);
    } else {
        return (text: string) => true;
    }
}

function createdSelector(time: string) {
    return new Date(time).getTime();
}

const typeFactions=5;

const searchTermMatcherSelector = createSelector(
    searchTermSelector,
    wildcardMatcher,
);
const categoryMatcherSelector = createSelector(
    categoriesSelector,
    (categories) => categories.qEmpty() ? () => true : (item: IEntity) => categories.qIncludes(item.categoryId || 0),
);

const participantMatcherSelector = createSelector(
    participantsSelector,
    (participants) => participants ? (participants.qEmpty() ? () => true : (item: IEntity) => isNote(item) && participants.qIncludes(item.userId)): () => true,
);

const participantGroupMatcherSelector = createSelector(
    participantgroupsSelector,
    (participantgroups) => participantgroups ? (participantgroups.qEmpty() ? () => true : (item: IEntity) => {    
        var groupsOfitem = isNote(item) && item.noteParticipantGroupIDs && item.noteParticipantGroupIDs.map((itm) => itm.toString());
        var selectedGroups = participantgroups && participantgroups.map((itm) => itm.toString());
        return groupsOfitem && selectedGroups && groupsOfitem.filter((obj) => {
            return selectedGroups.indexOf(obj.toString()) !== -1;
          }).qAny();
    }) : () => true,
);

const factionMatcherSelector = createSelector(
    factiongroupsSelector,
    usergroupResource.selectors.items,
    (factiongroups, usergroups) => factiongroups ? (factiongroups.qEmpty() ? () => true : (item: IQuestion) => {    
        var groupsOfitem = usergroups.qWhere((ug) => ug.userIDs.includes(item.userId)
        &&  ug.type===typeFactions).map((itm: IUsergroup) => itm.id.toString());
        var selectedGroups = factiongroups && factiongroups.map((itm) => itm.toString());
        return groupsOfitem && selectedGroups && groupsOfitem.filter((obj) => {
            return selectedGroups.indexOf(obj.toString()) !== -1;
          }).qAny();
    }) : () => true,
);

const categoryMatcherQuestionsSelector = createSelector(
    categoriesSelector,
    (categories) => categories.qEmpty() ? () => true : () => false,
);

const statusMatcherSelector = createSelector(
    statusSelector,
    (statuses) => 
    (statuses === undefined || statuses.qEmpty()) ? () => true : (item: IEntity) => 
        isQuestion(item) && statuses.map((z)=>z.valueOf()).qIncludes(item.status),
);

const originMatcherSelector = createSelector(
    originSelector,
    participantIdSelector,
    (origins, userid) => 
        origins.qEmpty() ? () => true : (item: IEntity) => {
           return isNote(item) && origins.qIncludes('mine' as Origin) && item.userId === (userid ? userid.toString():'') ||
               isNote(item) && origins.qIncludes('others' as Origin) && item.userId !== (userid ? userid.toString():'')  && (userid?userid.toString():'')!=='';
        },
);

const sortFuncSelector = createSelector(
    sortingSelector,
    (order) => {
        if (order === 'createdAsc') {
            // sorting createdAsc.
            return (a: IEntity, b: IEntity) => {
                if (createdSelector(a.created) !== createdSelector(b.created)) {
                    return createdSelector(b.created) - createdSelector(a.created);
                }
                return a.id - b.id;
            };
        } else if (order === 'pageNumber') {
            // sorting pageNumber.
            return (a: IEntity, b: IEntity) => {
                if (a.pageNumber !== b.pageNumber) {
                    return a.pageNumber - b.pageNumber;
                }
                if (createdSelector(a.created) !== createdSelector(b.created)) {
                    return createdSelector(b.created) - createdSelector(b.created);
                }
                return a.id - b.id;
            };
        } else {
            // sorting createdDesc.
            return (a: IEntity, b: IEntity) => {
                if (createdSelector(a.created) !== createdSelector(b.created)) {
                    return createdSelector(a.created) - createdSelector(b.created);
                }
                return b.id - a.id;
            };
        }
    },
);

const sortFuncEmailsSelector = createSelector(
    sortingSelector,
    (order) => {
        if (order === 'createdAsc') {
            // sorting createdAsc.
            return (a: IEmailMessage, b: IEmailMessage) => {
                if (createdSelector(a.dateEmail) !== createdSelector(b.dateEmail)) {
                    return createdSelector(b.dateEmail) - createdSelector(a.dateEmail);
                }
                return a.id - b.id;
            };
        } else {
            // sorting createdDesc.
            return (a: IEmailMessage, b: IEmailMessage) => {
                if (createdSelector(a.dateEmail) !== createdSelector(b.dateEmail)) {
                    return createdSelector(a.dateEmail) - createdSelector(b.dateEmail);
                }
                return b.id - a.id;
            };
        }
    },
);

// tslint:disable
export const sortFunction = (order: string) => {
    if (order === 'createdAsc') {
        // sorting createdAsc.
        return (a: object, b: object) => {
            if (createdSelector(a['created']) !== createdSelector(b['created'])) {
                return createdSelector(b['created']) - createdSelector(a['created']);
            }
            return a['id'] - b['id'];
        };
    } else if (order === 'pageNumber') {
        // sorting pageNumber.
        return (a: object, b: object) => {
            if (a['pageNumber'] !== b['pageNumber']) {
                return a['pageNumber'] - b['pageNumber'];
            }
            if (createdSelector(a['created']) !== createdSelector(b['created'])) {
                return createdSelector(b['created']) - createdSelector(b['created']);
            }
            return a['id'] - b['id'];
        };
    } else {
        // sorting createdDesc.
        return (a: object, b: object) => {
            if (createdSelector(a['created']) !== createdSelector(b['created'])) {
                return createdSelector(a['created']) - createdSelector(b['created']);
            }
            return b['id'] - a['id'];
        };
    }
};
// tslint:enable

export default {
    bookmarksItemsSelector: createSelector(
        bookmarksResource.selectors.items,
        searchTermMatcherSelector,
        categoryMatcherSelector,
        sortFuncSelector,
        (items, searchTermMatcher, categoryMatcher, sort) => {
            return items.qWhere((x) => searchTermMatcher(x.name)).qWhere(categoryMatcher).sort(sort);
        },
    ),
    categoriesItemsSelector: createSelector(
        categoriesResource.selectors.items,
        searchTermMatcherSelector,
        (items, searchTermMatcher) => {
            return items.qWhere((x) => searchTermMatcher(x.name));
        },
    ),
    notesItemsSelector: createSelector(
        notesResource.selectors.items,
        searchTermMatcherSelector,
        categoryMatcherSelector,
        participantMatcherSelector,
        participantGroupMatcherSelector,
        sortFuncSelector,
        originMatcherSelector,
        (items, searchTermMatcher, categoryMatcher,  participantMatcher, participantgroupMatcher, sort, originMatcher) => {
            return items.qWhere((x) => searchTermMatcher(x.text)).qWhere(categoryMatcher).qWhere(participantMatcher).qWhere(participantgroupMatcher).qWhere(originMatcher).sort(sort).qWhere((z) => !z.isquestion);
        },
    ),
    notesSharedSelector: createSelector(
        notesSharedResource.selectors.items,
        searchTermMatcherSelector,
        categoryMatcherSelector,
        participantMatcherSelector,
        participantGroupMatcherSelector,
        sortFuncSelector,
        originMatcherSelector,       
        (items, searchTermMatcher, categoryMatcher, participantMatcher, participantgroupMatcher, sort, originMatcher) => {
            return items.qWhere((x) => searchTermMatcher(x.text)).qWhere(categoryMatcher).qWhere(participantMatcher).qWhere(participantgroupMatcher).qWhere(originMatcher).sort(sort).qWhere((z)=>!z.isquestion);
        },
    ),

    questionsItemsSelector: createSelector(
        notesResource.selectors.items,
        notesSharedResource.selectors.items,
        questionsResource.selectors.items,
        beheerResource.selectors.items,
        usergroupResource.selectors.items,
        factionMatcherSelector,
        participantIdSelector,
        searchTermMatcherSelector,
        statusMatcherSelector,
        (itemsNotes, itemsNotesShared, itemsQuestions, beheer, usergroups, factionMatcher, userId, searchTermMatcher, statusMatcher) => {
            var arr: IQuestion[]= [];
            var admin = beheer && beheer.qFirst();
            if (admin && admin.factionvisible) {  
                // Normally the items are correcty filtered from the database
                // for development purposes the json-server won't
                // that's why this section is implemented for correct data           
                arr =  itemsNotes.concat(itemsNotesShared).qWhere((itm) => itm.isquestion).map((note) => 
                    itemsQuestions.some((q) => q.noteId === note.id) ? 
                    itemsQuestions.filter((q) => q.noteId === note.id).map((q) => ({q, note})) :                 
                        [{ q:{} as IQuestionDB, note }],
                    ).qWhere((z)=> !!z)
                    .qSelect((v)=> {
                        const combined = v[0];
                        const question: IQuestion = combined.note;
                        question.status = combined.q ? (combined.q as IQuestionDB).status:1;
                        question.antwoordConcern =combined.q ? (combined.q as IQuestionDB).antwoordConcern:'';    
                        question.parentId = combined.q ? (combined.q as IQuestionDB).parentId:undefined;      
                        question.practicionerId = combined.q ? (combined.q as IQuestionDB).practicionerId:undefined;           
                        return question; }).  
                    qWhere((question) => usergroups.qAny((ug) => ug.userIDs.includes(question.userId)
                                            && ug.userIDs.includes(!!userId?userId.toString():'') && ug.type===typeFactions || 
                                            ug.userIDs.includes(!!userId?userId.toString():'') && ug.type===typeRoleAdmin ||
                                            ug.userIDs.includes(!!userId?userId.toString():'') && ug.type===typeRolePracticioner || 
                                            ug.userIDs.includes(!!userId?userId.toString():'') && ug.type===typeRoleClerk ||                                                                                      
                                            question.status === statusGepubliceerd )).
                    sort((a, b) => { 
                        if (a && b) {
                            if (!a.parentId &&  !b.parentId && a.id>b.id ) {
                                return 1;
                            }   
                            if (!a.parentId &&  !b.parentId && a.id<b.id ) {
                                return -1;
                            }          
                            if (!!a.parentId && !!b.parentId && a.parentId > b.parentId) {
                                return 1;
                            }
                            if (!!a.parentId && !!b.parentId && a.parentId < b.parentId) {
                                return -1;
                            }                    
                            if (!!a.parentId && a.parentId >= b.id ) {
                                return 1;
                            }
                            if (!!a.parentId && a.parentId < b.id ) {
                                return -1;
                            }                                         
                            if (!!b.parentId && a.id <= b.parentId ) {
                                return -1;
                            }
                            if (!!b.parentId && a.id > b.parentId ) {
                                return 1;
                            }                                       
                            return 0;
                        }
                        } );
                return arr.qWhere((x) => searchTermMatcher(x.text)).qWhere(statusMatcher).qWhere(factionMatcher).qWhere((z) => z.isquestion);
            } else {                 
                arr =  itemsNotes.concat(itemsNotesShared).qWhere((itm) => itm.isquestion).map((note) => 
                    itemsQuestions.some((q) => q.noteId === note.id) ? 
                    itemsQuestions.filter((q) => q.noteId === note.id).map((q) => ({q, note})) :                 
                        [{ q:{} as IQuestionDB, note }],
                    ).qWhere((z)=> !!z)
                    .qSelect((v)=> {
                        const combined = v[0];
                        const question: IQuestion = combined.note;
                        question.status = combined.q ? (combined.q as IQuestionDB).status:1;
                        question.antwoordConcern =combined.q ? (combined.q as IQuestionDB).antwoordConcern:'';    
                        question.parentId = combined.q ? (combined.q as IQuestionDB).parentId:undefined;                
                        question.practicionerId = combined.q ? (combined.q as IQuestionDB).practicionerId:undefined;
                        return question; }).    
                    sort((a, b) => { 
                        if (a && b) {
                            if (!a.parentId &&  !b.parentId && a.id>b.id ) {
                                return 1;
                            }   
                            if (!a.parentId &&  !b.parentId && a.id<b.id ) {
                                return -1;
                            }          
                            if (!!a.parentId && !!b.parentId && a.parentId > b.parentId) {
                                return 1;
                            }
                            if (!!a.parentId && !!b.parentId && a.parentId < b.parentId) {
                                return -1;
                            }                    
                            if (!!a.parentId && a.parentId >= b.id ) {
                                return 1;
                            }
                            if (!!a.parentId && a.parentId < b.id ) {
                                return -1;
                            }                                         
                            if (!!b.parentId && a.id <= b.parentId ) {
                                return -1;
                            }
                            if (!!b.parentId && a.id > b.parentId ) {
                                return 1;
                            }                                       
                            return 0;
                        }
                        } );
                return arr.qWhere((x) => searchTermMatcher(x.text)).qWhere(statusMatcher).qWhere(factionMatcher).qWhere((z) => z.isquestion);
            }
        },            
    ),   
    
    participantsSelector:  participantsResource.selectors.items,

    EmailsSelector: createSelector(
        emailArchiveResource.selectors.items,
        searchTermMatcherSelector,
        sortFuncEmailsSelector,
        categoryMatcherQuestionsSelector,
        (items, searchTermMatcher, sort, categoryMatcherQuestion) => {
            return items.qWhere((x) => searchTermMatcher(x.htmlMail) || searchTermMatcher(x.subject))
            .qWhere(categoryMatcherQuestion)
            .sort(sort);
        },
    ), 

    usergroupSelector: createSelector(
        usergroupResource.selectors.items,
        searchTermMatcherSelector,
        (items, searchTermMatcher) => {
            return items.qWhere((x) => searchTermMatcher(x.name));
        },
    ),

    usergroupWitMeInItSelector: createSelector(
        usergroupResource.selectors.items,
        participantsResource.selectors.items,
        searchTermMatcherSelector,
        (items, participants, searchTermMatcher) => {
            let currentUser = participants.qWhere((i)=>i.isUser).qFirst();      
            const currentUserId =  currentUser ? currentUser.id: ''; 
            return items.qWhere((x) => searchTermMatcher(x.name) && x.userIDs && x.userIDs.includes(currentUserId));
        },
    ),
};



// WEBPACK FOOTER //
// (webpack)-remove-block-loader??ref--5-0!./src/note/admin/reselectors.tsx